import {
  EnterOutlined,
  MailOutlined,
  MessageOutlined,
  RobotOutlined
} from "@ant-design/icons"
import {
  Button,
  Card,
  Empty,
  Result,
  Segmented,
  Select,
  Skeleton,
  Typography
} from "antd"
import dayjs from "dayjs"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { getMessages } from "../api/messages.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import { CommunicateButton } from "../composer/CommunicateButton.tsx"
import type { ComposerTarget } from "../composer/composer-target.ts"
import type { RecordType } from "../types/compose.types.ts"
import MessageDetailDrawer from "../messages/MessageDetailDrawer.tsx"
import { MessageStatusTag } from "../messages/MessageStatusTag.tsx"
import type { MessageRow } from "../types/messages.types.ts"
import { onMessagesSent } from "../composer/messages-sent.ts"

export const TIMELINE_PAGE_SIZE = 20
export const TIMELINE_FILTERS = ["all", "email", "sms", "automated"] as const
type TimelineFilter = (typeof TIMELINE_FILTERS)[number]

export interface TimelineRecord {
  type: RecordType
  id: string
}

export interface RelatedRecordOption {
  record: TimelineRecord
  label: string
}

export const GENERAL_RELATED = "general"

export type TimelineMessage = MessageRow

const isAutomated = (message: MessageRow) => !message.sender

const recordLabel = (message: MessageRow) =>
  message.record?.label || message.record?.name || ""

const relatedKey = (record: TimelineRecord) => `${record.type}:${record.id}`

const ChannelIcon = ({ message }: { message: MessageRow }) => {
  const Icon = isAutomated(message)
    ? RobotOutlined
    : message.channel === "sms"
      ? MessageOutlined
      : MailOutlined
  return (
    <span
      aria-hidden
      className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#2A7D8C]/10 text-[var(--dk-primary-text,#2A7D8C)]"
    >
      <Icon />
    </span>
  )
}

export const groupByDay = <T extends MessageRow>(
  rows: T[],
  timezone: string
) => {
  const groups: Array<{ day: string; rows: T[] }> = []
  for (const row of rows) {
    const day = dayjs(row.created).tz(timezone).format("YYYY-MM-DD")
    const last = groups.at(-1)
    if (last?.day === day) last.rows.push(row)
    else groups.push({ day, rows: [row] })
  }
  return groups
}

const recordParams = (record: TimelineRecord) => ({
  record_type: record.type,
  record_id: record.id
})

export const timelineQuery = (
  record: TimelineRecord,
  filter: TimelineFilter,
  related: string | null,
  page: number
) => {
  const [type, id] = related?.split(":") ?? []
  return {
    ...(related && related !== GENERAL_RELATED && type && id
      ? recordParams({ type: type as RecordType, id })
      : recordParams(record)),
    ...(related === GENERAL_RELATED ? { general: "true" } : {}),
    ...(filter === "all" ? {} : { quick: filter }),
    page,
    page_size: TIMELINE_PAGE_SIZE
  }
}

interface RecordCommunicationTimelineProps {
  record: TimelineRecord
  composerTarget: ComposerTarget | null
  relatedRecords?: RelatedRecordOption[]
}

export const RecordCommunicationTimeline = ({
  record,
  composerTarget,
  relatedRecords
}: RecordCommunicationTimelineProps) => {
  const { t } = useTranslation()
  const { formatDate, timezone, dateFormat } = useDateFormatter()
  const dayLabel = (day: string) => {
    const today = dayjs().tz(timezone).format("YYYY-MM-DD")
    const yesterday = dayjs()
      .tz(timezone)
      .subtract(1, "day")
      .format("YYYY-MM-DD")
    if (day === today) return t("communication.timeline.today")
    if (day === yesterday) return t("communication.timeline.yesterday")
    return dayjs(day).format(dateFormat)
  }
  const [filter, setFilter] = useState<TimelineFilter>("all")
  const [related, setRelated] = useState<string | null>(null)
  const [rows, setRows] = useState<TimelineMessage[] | null>(null)
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [failed, setFailed] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [openMessage, setOpenMessage] = useState<string | null>(null)
  const generation = useRef(0)
  const recordKey = relatedKey(record)

  const load = useCallback(
    async (nextPage: number) => {
      const current = ++generation.current
      setFailed(false)
      if (nextPage === 1) setRows(null)
      else setLoadingMore(true)
      const [type, id] = recordKey.split(":")
      const response = await getMessages(
        timelineQuery(
          { type: type as RecordType, id },
          filter,
          related,
          nextPage
        )
      )
      if (current !== generation.current) return
      setLoadingMore(false)
      if (!response.ok || !response.data) {
        setFailed(true)
        return
      }
      const results = response.data.results as TimelineMessage[]
      setRows((previous) =>
        nextPage === 1 ? results : [...(previous ?? []), ...results]
      )
      setCount(response.data.count)
      setPage(nextPage)
    },
    [recordKey, filter, related]
  )

  useEffect(() => {
    void load(1)
  }, [load])

  useEffect(() => onMessagesSent(() => void load(1)), [load])

  const communicate = (
    <CommunicateButton
      size="middle"
      label={t("communication.composer.communicate")}
      target={composerTarget}
      onClosed={() => void load(1)}
    />
  )

  const body = () => {
    if (failed && !rows?.length) {
      return (
        <Result
          status="warning"
          title={t("communication.timeline.loadFailed")}
          extra={
            <Button onClick={() => load(1)}>{t("communication.retry")}</Button>
          }
        />
      )
    }
    if (rows === null) return <Skeleton active paragraph={{ rows: 4 }} />
    if (rows.length === 0) {
      return (
        <Empty
          description={
            filter === "all" && !related
              ? t("communication.timeline.empty")
              : t("communication.timeline.noMatch")
          }
        />
      )
    }
    return (
      <div
        aria-label={t("communication.timeline.title")}
        className="flex flex-col gap-3"
      >
        {groupByDay(rows, timezone).map((group) => {
          const label = dayLabel(group.day)
          return (
            <section key={group.day} aria-label={label}>
              <Typography.Text
                type="secondary"
                className="text-xs font-semibold uppercase"
              >
                {label}
              </Typography.Text>
              <ol
                aria-label={label}
                className="m-0 flex list-none flex-col p-0"
              >
                {group.rows.map((message) => (
                  <TimelineRow
                    key={message.id}
                    message={message}
                    time={formatDate(message.created, "HH:mm") ?? ""}
                    replyTime={
                      message.latest_reply
                        ? (formatDate(
                            message.latest_reply.received_at,
                            "HH:mm"
                          ) ?? "")
                        : ""
                    }
                    onOpen={() => setOpenMessage(message.id)}
                  />
                ))}
              </ol>
            </section>
          )
        })}
      </div>
    )
  }

  return (
    <Card
      size="small"
      title={t("communication.timeline.title")}
      extra={communicate}
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Segmented<TimelineFilter>
          value={filter}
          onChange={setFilter}
          options={TIMELINE_FILTERS.map((value) => ({
            value,
            label: t(`communication.timeline.filters.${value}`)
          }))}
        />
        {relatedRecords && relatedRecords.length > 0 && (
          <Select
            allowClear
            className="w-full sm:w-64"
            aria-label={t("communication.timeline.relatedTo")}
            placeholder={t("communication.timeline.relatedTo")}
            value={related}
            onChange={(value?: string) => setRelated(value ?? null)}
            options={[
              {
                value: GENERAL_RELATED,
                label: t("communication.timeline.general")
              },
              ...relatedRecords.map((option) => ({
                value: relatedKey(option.record),
                label: option.label
              }))
            ]}
          />
        )}
      </div>
      {body()}
      {rows && rows.length < count && (
        <div className="mt-3 flex justify-center">
          <Button loading={loadingMore} onClick={() => load(page + 1)}>
            {t("communication.timeline.loadMore", {
              shown: rows.length,
              total: count
            })}
          </Button>
        </div>
      )}
      <MessageDetailDrawer
        messageId={openMessage}
        onClose={() => setOpenMessage(null)}
      />
    </Card>
  )
}

const TimelineRow = ({
  message,
  time,
  replyTime,
  onOpen
}: {
  message: TimelineMessage
  time: string
  replyTime: string
  onOpen: () => void
}) => {
  const { t } = useTranslation()
  const reply = message.latest_reply
  return (
    <li className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full cursor-pointer items-start gap-3 bg-transparent px-1 py-3 text-left hover:bg-gray-50"
      >
        <ChannelIcon message={message} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2">
            <Typography.Text strong className="truncate">
              {message.subject ||
                (message.channel === "sms"
                  ? t("communication.messages.textMessage")
                  : "—")}
            </Typography.Text>
            <MessageStatusTag status={message.status} />
            {message.reply_count > 0 && (
              <Typography.Text type="secondary" className="text-xs">
                {t("communication.messages.replies", {
                  count: message.reply_count
                })}
              </Typography.Text>
            )}
          </span>
          {message.body_preview && (
            <Typography.Text type="secondary" className="line-clamp-1 text-sm">
              {message.body_preview}
            </Typography.Text>
          )}
          <Typography.Text type="secondary" className="text-xs">
            {t("communication.timeline.meta", {
              to: message.contact_name || message.to_address || "—",
              sender:
                message.sender_name ?? t("communication.messages.automated")
            })}
            {recordLabel(message) ? ` · ${recordLabel(message)}` : ""}
          </Typography.Text>
        </span>
        <Typography.Text type="secondary" className="shrink-0 text-xs">
          {time}
        </Typography.Text>
      </button>
      {reply && (
        <div
          aria-label={t("communication.timeline.latestReply")}
          className="mb-3 ml-11 flex gap-2 rounded-lg border border-solid border-[#2A7D8C]/20 bg-[#2A7D8C]/5 p-3"
        >
          <EnterOutlined
            aria-hidden
            className="mt-1 -scale-x-100 text-[var(--dk-primary-text,#2A7D8C)]"
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <Typography.Text className="text-xs">
              <Typography.Text
                strong
                className="text-xs text-[var(--dk-primary-text,#2A7D8C)]"
              >
                {t("communication.timeline.replied", {
                  name: reply.from_name || reply.from_address
                })}
              </Typography.Text>
              {replyTime ? ` · ${replyTime}` : ""}
            </Typography.Text>
            {reply.body_preview && (
              <Typography.Text className="line-clamp-2 text-sm">
                {reply.body_preview}
              </Typography.Text>
            )}
            {reply.forwarded_to.length > 0 && (
              <Typography.Text type="secondary" className="text-xs">
                {t("communication.timeline.forwardedTo", {
                  to: reply.forwarded_to.join(", ")
                })}
              </Typography.Text>
            )}
            <Button
              type="link"
              size="small"
              className="self-start !px-0"
              onClick={onOpen}
            >
              {t("communication.timeline.viewConversation")}
            </Button>
          </div>
        </div>
      )}
    </li>
  )
}

export const useReadsMessages = () => {
  const rights = useCommunicationRights()
  return rights.send || rights.viewAll
}
